import './HvacFormSectionContent.scss';
import { FieldPath } from '@form-ts/core';
import { useFormField } from '@form-ts/react';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { EnergyEfficiencyLabel } from 'src/modules/common/components/EnergyEfficiencyLabel';
import { NoteBadge } from 'src/modules/common/components/NoteBadge';
import { LABELS } from 'src/modules/common/constants/energyEfficiencyLabels';
import { BodySmall02 } from 'src/modules/common/typography/BodySmall02';
import { FormError } from 'src/modules/form/types/FormError';
import { HvacConfigOption, HvacConfigSection } from 'src/modules/hvac/types/HvacConfig';
import { HvacFormData, HvacFormSectionAnswerData } from 'src/modules/hvac/types/HvacFormData';
import { HvacFormSectionFormBlock } from 'src/modules/hvac/views/HvacFormSectionFormBlock';
import { HvacFormSetupField } from 'src/modules/hvac/views/HvacFormSetupField';
import { HvacFormSetupOption } from 'src/modules/hvac/views/HvacFormSetupOption';

type Props = {
  readonly path: FieldPath<HvacFormData, FormError, HvacFormSectionAnswerData>;
  readonly disabled: boolean;
  readonly readOnly: boolean;
  readonly config: HvacConfigSection;
  readonly hasImpact: boolean;
};

export const HvacFormSectionContent = ({
  path,
  disabled,
  readOnly,
  config,
  hasImpact,
}: Props): React.ReactElement => {
  const intl = useIntl();

  const { value: answer } = useFormField(path);

  return (
    <div
      className="bp-hvac-form-section-content"
    >
      {!hasImpact && (
        <NoteBadge content={<FormattedMessage id="hvac/form/section/weightless"/>}/>
      )}
      <div className="bp-hvac-form-section-content__title-wrapper">
        <BodySmall02 className="bp-hvac-form-section-content__title">
          {config.explanation !== null && (
            <FormattedMessage id={config.explanation}/>
          )}
        </BodySmall02>
      </div>

      <HvacFormSectionFormBlock
        title={<FormattedMessage id="hvac/form/common/actual"/>}
        label={config.has_impact ? renderEnergyEfficiencyLabel(answer.actual.option) : null}
      >
        <HvacFormSetupField
          options={config.option}
          field={path.at('actual').at('option')}
          disabled={disabled}
          title={intl.formatMessage({ id: 'hvac/form/setup/actual' })}
          renderOption={config.has_impact ? renderSetupOption : renderNoImpactOption}
          renderSelectedOption={config.has_impact ? renderSetupValue : renderNoImpactValue}
          readOnly={readOnly}
        />

      </HvacFormSectionFormBlock>

      <HvacFormSectionFormBlock
        title={<FormattedMessage id="hvac/form/common/future"/>}
        label={config.has_impact ? renderEnergyEfficiencyLabel(answer.future.option) : null}
      >
        <HvacFormSetupField
          options={config.option}
          field={path.at('future').at('option')}
          disabled={disabled}
          title={intl.formatMessage({ id: 'hvac/form/setup/future' })}
          renderOption={config.has_impact ? renderSetupOption : renderNoImpactOption}
          renderSelectedOption={config.has_impact ? renderSetupValue : renderNoImpactValue}
          readOnly={readOnly}
        />

      </HvacFormSectionFormBlock>
    </div>
  );
};

function renderSetupOption(
  option: HvacConfigOption,
): React.ReactElement {
  return (
    <HvacFormSetupOption
      label={<FormattedMessage id={option.title}/>}
      comment={option.explanation && <FormattedMessage id={option.explanation}/>}
      renderComment={true}
      renderEfficiencyLabel={true}
      energyEfficiencyClass={option.class}
    />
  );
}

function renderSetupValue(
  option: HvacConfigOption,
): React.ReactElement {
  return (
    <HvacFormSetupOption
      label={<FormattedMessage id={option.title}/>}
      comment={option.explanation && <FormattedMessage id={option.explanation}/>}
      renderComment={false}
      renderEfficiencyLabel={false}
    />
  );
}

function renderNoImpactOption(
  option: HvacConfigOption,
): React.ReactElement {
  return (
    <HvacFormSetupOption
      label={<FormattedMessage id={option.title}/>}
      comment={option.explanation && <FormattedMessage id={option.explanation}/>}
      renderComment={false}
      renderEfficiencyLabel={false}
      energyEfficiencyClass={option.class}
    />
  );
}

function renderNoImpactValue(
  option: HvacConfigOption,
): React.ReactElement {
  return <FormattedMessage id={option.title}/>;
}

function renderEnergyEfficiencyLabel(
  option: HvacConfigOption | null,
): React.ReactElement {
  const level = option?.class ?? null;

  return (
    <EnergyEfficiencyLabel value={level} fixed={true} size="sm">
      {level === null ? '\u2013' : LABELS[level]}
    </EnergyEfficiencyLabel>
  );
}
